import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight, Download } from "lucide-react";
import { useAuth } from "@clerk/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Btn, Card, Eyebrow, Field } from "@/components/kit";
import { RequireSignIn } from "@/pages/platform/MemberPages";
import { ProductStatus } from "@/components/platform/Products";
import Admin from "@/pages/Admin";
import { useApi, API } from "@/lib/api";

const EMPTY = { slug: "", name: "", kind: "application", status: "soon", tagline: "", description: "", price_label: "", highlights: [], cta_path: "", order: 99, visible: true };
const KIND = { application: "Application", toolbox: "Boîte à outils" };

function Select({ label, value, onChange, options, testId }) {
  return (
    <label className="block space-y-2">
      <span className="block text-sm font-medium text-ink">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full rounded-xl border border-line bg-white px-4 py-3 text-base outline-none focus:border-bx focus:ring-2 focus:ring-bx/20">
        {Object.entries(options).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

function ProductForm({ initial, isNew, onSaved, onClose }) {
  const api = useApi();
  const [f, setF] = useState({ ...initial, highlightsText: (initial.highlights || []).join("\n") });
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    const { highlightsText, ...rest } = f;
    const body = { ...rest, order: Number.isFinite(Number(f.order)) && String(f.order).trim() !== "" ? Number(f.order) : 99, highlights: highlightsText.split("\n") };
    try {
      await api(isNew ? "POST" : "PUT", isNew ? "/admin/products" : `/admin/products/${initial.slug}`, body);
      toast.success(isNew ? "Produit ajouté" : "Produit mis à jour");
      onSaved();
    } catch (e) {
      toast.error(e.message || "Enregistrement impossible");
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4" data-testid="product-form">
      {isNew && <Field label="Identifiant (utilisé dans l'adresse)" hint="Ex. : collaby, guide-scripts" value={f.slug} onChange={set("slug")} testId="pf-slug" />}
      <Field label="Nom" value={f.name} onChange={set("name")} testId="pf-name" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Select label="Type" value={f.kind} onChange={set("kind")} options={KIND} testId="pf-kind" />
        <Select label="Statut" value={f.status} onChange={set("status")} options={{ available: "Disponible", soon: "Bientôt disponible" }} testId="pf-status" />
      </div>
      <Field label="Accroche" value={f.tagline} onChange={set("tagline")} testId="pf-tagline" />
      <Field label="Description" multiline value={f.description} onChange={set("description")} testId="pf-description" />
      <Field label="Points forts (un par ligne)" multiline value={f.highlightsText} onChange={set("highlightsText")} testId="pf-highlights" />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Prix affiché" hint="Ex. : 9 €" value={f.price_label} onChange={set("price_label")} testId="pf-price" />
        <Field label="Ordre d'affichage" value={String(f.order)} onChange={set("order")} testId="pf-order" />
      </div>
      <Field label="Lien du produit" hint="Page interne (/ugc-go) ou application externe (https://…). Vide si bientôt disponible." value={f.cta_path} onChange={set("cta_path")} testId="pf-link" />
      <label className="flex items-center gap-3 text-sm text-ink">
        <input type="checkbox" checked={f.visible} onChange={(e) => set("visible")(e.target.checked)} className="h-4 w-4 accent-[#651F38]" data-testid="pf-visible" />
        Visible sur le site
      </label>
      {initial.slug === "ugc-go" && <p className="rounded-xl bg-gold-soft/60 px-4 py-3 text-xs text-[#5E4A2C]">Le prix réellement facturé pour UGC GO est fixé dans Stripe (9 €). Le prix affiché ici doit rester identique.</p>}
      <div className="flex justify-end gap-3 pt-2">
        <Btn variant="ghost" onClick={onClose} data-testid="pf-cancel">Annuler</Btn>
        <Btn onClick={save} disabled={busy} data-testid="pf-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Enregistrer</Btn>
      </div>
    </div>
  );
}

function ProductsTab() {
  const api = useApi();
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api("GET", "/admin/products").then((r) => setItems(r.products)).catch(() => toast.error("Chargement impossible")), [api]);
  useEffect(() => { load(); }, [load]);
  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.name} » du catalogue ?`)) return;
    try { await api("DELETE", `/admin/products/${p.slug}`); toast.success("Produit supprimé"); load(); }
    catch (e) { toast.error(e.message || "Suppression impossible"); }
  };
  return (
    <div data-testid="admin-products">
      <div className="mb-4 flex justify-end"><Btn onClick={() => setEdit({ p: EMPTY, isNew: true })} data-testid="admin-product-add"><Plus className="h-4 w-4" />Ajouter un produit</Btn></div>
      <Card className="p-0 sm:p-0">
        {!items ? <p className="p-6 text-soft">Chargement…</p> : (
          <ul>
            {items.map((p) => (
              <li key={p.slug} className="flex flex-col gap-3 border-b border-line px-5 py-4 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:px-7" data-testid={`admin-product-${p.slug}`}>
                <div className="min-w-0">
                  <p className="font-medium text-ink">{p.name} <span className="text-xs text-soft">· {KIND[p.kind]}{p.visible ? "" : " · masqué"}</span></p>
                  <div className="mt-1"><ProductStatus p={p} /></div>
                </div>
                <div className="flex gap-2">
                  <Btn variant="outline" size="sm" onClick={() => setEdit({ p, isNew: false })} data-testid={`admin-product-edit-${p.slug}`}><Pencil className="h-4 w-4" />Modifier</Btn>
                  {p.slug !== "ugc-go" && <Btn variant="ghost" size="sm" onClick={() => remove(p)} data-testid={`admin-product-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></Btn>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] w-[calc(100%-2rem)] max-w-xl overflow-y-auto rounded-[20px]">
          <DialogHeader><DialogTitle className="font-display text-2xl text-bx">{edit?.isNew ? "Nouveau produit" : `Modifier ${edit?.p.name}`}</DialogTitle></DialogHeader>
          {edit && <ProductForm initial={edit.p} isNew={edit.isNew} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const STATUS = { paid: "Payée", pending: "En attente", expired: "Expirée", failed: "Échouée", refunded: "Remboursée" };

function SalesSummary() {
  const api = useApi();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const { getToken } = useAuth();
  useEffect(() => {
    setS(null);
    api("GET", `/admin/stats?month=${month}`).then(setS).catch(() => setS({ error: true }));
  }, [api, month]);
  const shift = (d) => {
    const [y, m] = month.split("-").map(Number);
    const dt = new Date(y, m - 1 + d, 1);
    setMonth(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`);
  };
  const label = new Date(`${month}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  const euro = (v) => `${(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const exportXlsx = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/orders/export?month=${month}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = Object.assign(document.createElement("a"), { href: url, download: `ugcetco-commandes-${month}.xlsx` });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Export impossible pour le moment"); }
    setBusy(false);
  };
  const stats = s && !s.error ? [
    ["Ventes", s.sales, `${s.self_sales} pour elles-mêmes · ${s.gift_sales} en cadeau`, "sales"],
    ["Chiffre d'affaires", euro(s.revenue), "Montants TTC encaissés", "revenue"],
    ["Cadeaux utilisés", s.gifts_redeemed, "Codes cadeaux activés ce mois-ci", "redeemed"],
  ] : [];
  return (
    <div className="mb-5" data-testid="sales-summary">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="font-display text-xl font-semibold capitalize text-bx" data-testid="sales-month">{label}</p>
        <div className="flex items-center gap-2">
          <Btn variant="outline" size="sm" onClick={exportXlsx} disabled={busy} aria-label="Exporter le mois en Excel" className="disabled:text-bx" data-testid="sales-export">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}<span className="hidden sm:inline">Exporter (Excel)</span></Btn>
          <button type="button" onClick={() => shift(-1)} aria-label="Mois précédent" className="rounded-full border border-line bg-white p-2 text-bx hover:border-bx/40" data-testid="sales-prev"><ChevronLeft className="h-4 w-4" /></button>
          <button type="button" onClick={() => shift(1)} aria-label="Mois suivant" className="rounded-full border border-line bg-white p-2 text-bx hover:border-bx/40" data-testid="sales-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
      {!s ? <p className="text-soft">Chargement…</p> : s.error ? <p className="text-soft" data-testid="sales-error">Impossible de charger le résumé.</p> : (
        <div className="grid gap-3 sm:grid-cols-3">
          {stats.map(([t, v, sub, id]) => (
            <Card key={id} className="!p-5" data-testid={`sales-stat-${id}`}>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-bx-2">{t}</p>
              <p className="mt-2 font-display text-3xl font-semibold text-bx" data-testid={`sales-stat-${id}-value`}>{v}</p>
              <p className="mt-1 text-sm text-soft">{sub}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function OrdersTab() {
  const api = useApi();
  const [orders, setOrders] = useState(null);
  useEffect(() => { api("GET", "/admin/orders").then((r) => setOrders(r.orders)).catch(() => setOrders([])); }, [api]);
  return (
    <Card className="p-0 sm:p-0" data-testid="admin-orders">
      {!orders ? <p className="p-6 text-soft">Chargement…</p> : orders.length === 0 ? <p className="p-6 text-soft" data-testid="admin-orders-empty">Aucune commande pour l'instant.</p> : (
        <ul>
          {orders.map((o) => (
            <li key={o.session_id} className="flex flex-col gap-1 border-b border-line px-5 py-4 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:px-7">
              <div className="min-w-0">
                <p className="truncate font-medium text-ink">{o.email || "Compte inconnu"}</p>
                <p className="text-xs text-soft">{o.product}{o.kind === "gift" ? " · cadeau" : ""} · {o.created_at ? new Date(o.created_at).toLocaleDateString("fr-FR") : ""}</p>
              </div>
              <p className="text-sm"><span className="font-semibold text-ink">{o.amount != null ? `${o.amount.toLocaleString("fr-FR", { minimumFractionDigits: 2 })} €` : ""}</span> <span className={o.payment_status === "paid" ? "text-bx" : "text-soft"}>· {STATUS[o.payment_status] || o.payment_status}</span></p>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Broadcast({ items, onSent }) {
  const api = useApi();
  const [topic, setTopic] = useState("collaby");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = items.filter((w) => (w.interests || []).includes(topic) && !(w.notified || []).includes(topic)).length;
  const send = async () => {
    if (!window.confirm(`Envoyer l'annonce d'ouverture « ${WL_LABEL[topic]} » à ${pending} personne${pending > 1 ? "s" : ""} ?`)) return;
    setBusy(true);
    try {
      const r = await api("POST", "/admin/waitlist/broadcast", { topic, message, origin_url: window.location.origin });
      const msg = `${r.sent} email${r.sent > 1 ? "s" : ""} envoyé${r.sent > 1 ? "s" : ""}${r.failed ? `, ${r.failed} en échec` : ""}`;
      if (r.remaining > 0) toast.warning(`${msg}. Il reste ${r.remaining} personne${r.remaining > 1 ? "s" : ""} à prévenir : relance l'envoi dans quelques minutes.`);
      else toast.success(msg);
      setMessage("");
      onSent();
    } catch (e) {
      toast.error(e.message || "Envoi impossible");
    }
    setBusy(false);
  };
  return (
    <Card className="mb-5" data-testid="admin-broadcast">
      <p className="font-display text-xl font-semibold text-bx">Annoncer une ouverture</p>
      <p className="mt-1 text-sm text-soft">Un email est envoyé une seule fois aux personnes inscrites pour ce sujet, avec un lien vers sa page.</p>
      <div className="mt-5 space-y-4">
        <Select label="Ce qui ouvre" value={topic} onChange={setTopic} options={WL_LABEL} testId="broadcast-topic" />
        <Field label="Message personnel (facultatif)" hint="800 caractères maximum" multiline value={message} onChange={(v) => setMessage(v.slice(0, 800))} testId="broadcast-message" />
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-soft" data-testid="broadcast-count">{pending} personne{pending > 1 ? "s" : ""} à prévenir</p>
          <Btn onClick={send} disabled={busy || pending === 0} data-testid="broadcast-send">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Envoyer l'annonce</Btn>
        </div>
      </div>
    </Card>
  );
}

const WL_LABEL = { collaby: "Collaby", "boite-a-outils": "Boîte à outils", academie: "Académie" };

function WaitlistTab() {
  const api = useApi();
  const [items, setItems] = useState(null);
  const load = useCallback(() => api("GET", "/admin/waitlist").then((r) => setItems(r.entries)).catch(() => setItems([])), [api]);
  useEffect(() => { load(); }, [load]);
  const remove = async (email) => {
    if (!window.confirm(`Retirer ${email} de la liste d'attente ?`)) return;
    try { await api("DELETE", `/admin/waitlist/${encodeURIComponent(email)}`); toast.success("Inscription retirée"); load(); }
    catch (e) { toast.error(e.message || "Suppression impossible"); }
  };
  const LABEL = WL_LABEL;
  return (
    <>
    {items && (
      <div className="mb-5 grid gap-3 sm:grid-cols-3" data-testid="waitlist-stats">
        {Object.entries(WL_LABEL).map(([id, label]) => {
          const total = items.filter((w) => (w.interests || []).includes(id)).length;
          const pending = items.filter((w) => (w.interests || []).includes(id) && !(w.notified || []).includes(id)).length;
          return (
            <Card key={id} className="!p-5" data-testid={`waitlist-stat-${id}`}>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-bx-2">{label}</p>
              <p className="mt-2 font-display text-4xl font-semibold text-bx" data-testid={`waitlist-stat-${id}-total`}>{total}</p>
              <p className="mt-1 text-sm text-soft">{total > 1 ? "personnes en attente" : "personne en attente"}{total > 0 && ` · ${pending} à prévenir`}</p>
            </Card>
          );
        })}
      </div>
    )}
    {items && items.length > 0 && <Broadcast items={items} onSent={load} />}
    <Card className="p-0 sm:p-0" data-testid="admin-waitlist">
      {!items ? <p className="p-6 text-soft">Chargement…</p> : items.length === 0 ? <p className="p-6 text-soft" data-testid="admin-waitlist-empty">Aucune inscription pour l'instant.</p> : (
        <>
          <p className="border-b border-line px-5 py-3 text-sm text-soft sm:px-7">{items.length} inscription{items.length > 1 ? "s" : ""}</p>
          <ul>
            {items.map((w) => (
              <li key={w.email} className="flex flex-col gap-1 border-b border-line px-5 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:px-7">
                <span className="truncate font-medium text-ink">{w.email}</span>
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-soft">
                  <span>{(w.interests || []).map((i) => LABEL[i] || i).join(" · ")} · {new Date(w.created_at).toLocaleDateString("fr-FR")}</span>
                  {(w.notified || []).length > 0 && <span className="whitespace-nowrap text-bx">· prévenue : {w.notified.map((i) => LABEL[i] || i).join(", ")}</span>}
                  <button type="button" onClick={() => remove(w.email)} aria-label={`Retirer ${w.email}`} className="rounded-full p-1.5 text-soft hover:bg-ivory hover:text-bx" data-testid={`waitlist-remove-${w.email}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
    </>
  );
}

export default function PlatformAdmin() {
  return (
    <RequireSignIn>
      {(me) => me.role !== "admin" ? <Navigate to="/mon-espace" replace /> : (
        <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8 md:py-16" data-testid="platform-admin">
          <Eyebrow>Administration</Eyebrow>
          <h1 className="mt-4 text-4xl font-semibold text-bx">Gérer UGC & CO</h1>
          <p className="mt-3 text-soft">Produits du catalogue, membres et commandes. La gestion des contenus arrivera avec la Boîte à outils et l'Académie.</p>
          <Tabs defaultValue="products" className="mt-8">
            <TabsList className="h-auto flex-wrap rounded-full bg-white p-1 ring-1 ring-line">
              <TabsTrigger value="products" className="rounded-full px-5 py-2 data-[state=active]:bg-bx data-[state=active]:text-white" data-testid="admin-tab-products">Produits</TabsTrigger>
              <TabsTrigger value="members" className="rounded-full px-5 py-2 data-[state=active]:bg-bx data-[state=active]:text-white" data-testid="admin-tab-members">Membres</TabsTrigger>
              <TabsTrigger value="orders" className="rounded-full px-5 py-2 data-[state=active]:bg-bx data-[state=active]:text-white" data-testid="admin-tab-orders">Commandes</TabsTrigger>
              <TabsTrigger value="waitlist" className="rounded-full px-5 py-2 data-[state=active]:bg-bx data-[state=active]:text-white" data-testid="admin-tab-waitlist">Liste d'attente</TabsTrigger>
            </TabsList>
            <TabsContent value="products" className="mt-6"><ProductsTab /></TabsContent>
            <TabsContent value="members" className="mt-6"><Admin me={me} /></TabsContent>
            <TabsContent value="orders" className="mt-6"><SalesSummary /><OrdersTab /></TabsContent>
            <TabsContent value="waitlist" className="mt-6"><WaitlistTab /></TabsContent>
          </Tabs>
        </div>
      )}
    </RequireSignIn>
  );
}
